import { SearchModel } from './common';

/**
 * Tipos para Notas Fiscais conforme schema [Financial].[Invoice] / [Financial].[InvoiceItem]
 *
 * Status (situação da nota fiscal):
 *   1 = Pendente          — nota criada, ainda não enviada ao SEFAZ (editável)
 *   2 = Cancelada         — nota cancelada
 *   3 = Aguardando recibo — aguardando retorno do SEFAZ
 *   4 = Rejeitada         — nota rejeitada pelo SEFAZ
 *   5 = Autorizada        — nota autorizada pelo SEFAZ
 *   6 = Emitida DANFE     — DANFE emitida
 *   7 = Registrada        — nota registrada
 *   8 = Aguardando protocolo — em processamento na SEFAZ (lote 103/105); número reservado,
 *       concluída automaticamente pelo processo nfe_pending_authorization
 *   9 = Denegada          — nota denegada pelo SEFAZ
 *  10 = Consulta situação — em consulta de situação
 *  11 = Bloqueada         — nota bloqueada
 */

// ─── Status Options ──────────────────────────────────────────────

export const INVOICE_STATUS_OPTIONS = [
  { value: 1, label: 'Pendente', color: 'yellow' },
  { value: 2, label: 'Cancelada', color: 'red' },
  { value: 3, label: 'Aguardando Recibo', color: 'orange' },
  { value: 4, label: 'Rejeitada', color: 'red' },
  { value: 5, label: 'Autorizada', color: 'green' },
  { value: 6, label: 'Emitida DANFE', color: 'emerald' },
  { value: 7, label: 'Registrada', color: 'blue' },
  { value: 8, label: 'Aguardando Protocolo', color: 'orange' },
  { value: 9, label: 'Denegada', color: 'slate' },
  { value: 10, label: 'Consulta Situação', color: 'cyan' },
  { value: 11, label: 'Bloqueada', color: 'purple' },
] as const;

// ─── Model Options ───────────────────────────────────────────────
// Modelo fiscal da nota — coluna [Financial].[Invoice].[Model]
//   55 = NF-e (padrão)
//   65 = NFC-e

export const INVOICE_MODEL_OPTIONS = [
  { value: 55, label: 'NF-e (55)' },
  { value: 65, label: 'NFC-e (65)' },
] as const;

export const INVOICE_MODEL_DEFAULT = 55;

export const getInvoiceModelLabel = (model?: number | null): string => {
  if (model === null || model === undefined) return '—';
  return INVOICE_MODEL_OPTIONS.find((opt) => opt.value === model)?.label ?? String(model);
};

// ─── Invoice Item ────────────────────────────────────────────────

export interface InvoiceItem {
  invoice_item_id?: number;
  invoice_id?: number;
  code?: string;
  description?: string;
  unit?: string;
  quantity: number;
  unit_value: number;
  total_value?: number;
  type?: string;
  gross_weight?: number;
  net_weight?: number;
  purchase_order_reference?: string;
  ncm?: string;
  cest?: string;
  service_code?: string;
  origin?: number;
  gtin?: string;
  cfop?: string;
  additional_info?: string;
  approx_total_tax?: number;
  icms_st?: number;
  icms_origin?: number;
  icms_modality?: number;
  icms_rate?: number;
  icms_value?: number;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  last_updated_by?: string;
}

// ─── Parcelas (formas de pagamento) ──────────────────────────────

export interface InvoiceInstallment {
  invoice_installment_id: number;
  invoice_id: number;
  value: number;
  due_date?: string;
  payment_method_id?: number;
  payment_method_name?: string;
  notes?: string;
  created_at?: string;
}

// ─── Invoice (cabeçalho) ─────────────────────────────────────────

export interface Invoice {
  invoice_id: number;
  company_id: number;
  number: number;
  serie: number;
  /**
   * Modelo fiscal da NF (55 = NF-e, 65 = NFC-e).
   * Default: 55. Campo não editável após a criação.
   */
  model?: number;
  access_key?: string;
  /** Tipo de operação: 'E' = Entrada, 'S' = Saída — coluna [Type] da tabela [Financial].[Invoice] */
  type?: string;
  status?: number;
  customer_id?: number;
  generation_timestamp: string;
  total_amount?: number;
  erp_invoice_id?: number;
  customer_address?: string;
  customer_phone?: string;
  order_num_ecommerce?: number;
  customer_name?: string;
  shipping_service_name?: string;
  volume_quantity?: number;
  total_weight?: number;
  ship_addr_street?: string;
  ship_addr_num?: string;
  ship_addr_compl?: string;
  ship_addr_district?: string;
  ship_addr_city?: string;
  ship_addr_state?: string;
  ship_addr_zip?: string;
  ship_cust_email?: string;
  customer_document?: string;
  product_amount?: number;
  send_to_shipping_provider_id?: number;
  is_simple_tax?: boolean;
  invoice_transaction_type_id?: number;
  invoice_transaction_type_name?: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  last_updated_by?: string;
  provider_id?: number | null;
  provider_service_id?: number | null;
  // Empresa emissora
  company_name?: string;
  company_document?: string;
  // Sub-resources
  items?: InvoiceItem[];
  // Return invoice linkage (populated by backend via InvoiceReference)
  /** ID of the return invoice linked to this sales invoice (if any) */
  return_invoice_id?: number;
  /** Number of the return invoice linked to this sales invoice (if any) */
  return_invoice_number?: number;
  /** Sentido inverso: quando ESTA nota é a devolução, a NF de venda que ela referencia. */
  referenced_invoice_id?: number | null;
  referenced_invoice_number?: number | null;
  referenced_invoice_access_key?: string | null;
  /** Listas completas do vínculo, preenchidas só no detalhe (GET /invoices/:id). */
  return_invoices?: InvoiceLink[];
  referenced_invoices?: InvoiceLink[];
}

/** Nota relacionada a outra por [Financial].[InvoiceReference] (devolução ↔ venda). */
export interface InvoiceLink {
  invoice_id: number;
  number: number;
  serie: number;
  type?: string | null;
  access_key?: string | null;
  status?: string | null;
  status_id?: number | null;
  total_amount?: number | null;
  generation_timestamp?: string | null;
}

// ─── Search Model ────────────────────────────────────────────────

export interface InvoiceSearchModel extends SearchModel {
  company_id?: number;
  status?: number;
  /** Filtro multi-select por status da nota fiscal (StatusId) */
  status_ids?: number[];
  /** Filtro por modelo fiscal: 55 = NF-e, 65 = NFC-e */
  model?: number;
  date_from?: string;
  date_to?: string;
  customer_name?: string;
  /** Filtro por tipo de transação da natureza da operação: 'E' = Entrada, 'S' = Saída */
  transaction_type?: string;
  /** Filtro por cliente final da natureza da operação: true/false */
  final_customer?: boolean;
  /** Filtro multi-select por natureza da operação (InvoiceTransactionTypeId) */
  invoice_transaction_type_ids?: number[];
}

export interface InvoiceShippingOrderCandidate {
  invoice_id: number;
  company_id: number;
  company_name?: string;
  company_document?: string;
  number: number;
  serie: number;
  generation_timestamp: string;
  customer_name?: string;
  customer_document?: string;
  total_amount?: number | null;
  provider_id?: number | null;
  provider_service_id?: number | null;
  quote_provider_id?: number | null;
  quote_provider_service_id?: number | null;
  shipping_order_id?: string | null;
  integrated_provider_id?: number | null;
  integrated_provider_service_id?: number | null;
  provider_name?: string | null;
  provider_service_name?: string | null;
  is_integrated: boolean;
  has_order_quote_selection: boolean;
  /** Invoice status (1=Pendente, 4=Rejeitada, 5=Autorizada, etc.) */
  invoice_status?: number | null;
}

export interface InvoiceShippingOrderSearchModel {
  date: string;
  page: number;
  pageSize: number;
  search?: string;
  company_id?: number;
  provider_id?: number;
  integration_status?: 'pending' | 'integrated' | 'all';
}

export interface InvoiceShippingAssignmentPayload {
  provider_id: number | null;
  provider_service_id: number | null;
}

export interface InvoiceShippingOrderSendPayload {
  provider_id: number;
  provider_service_id: number;
}

export interface InvoiceShippingOrderSendResult {
  invoice_id: number;
  provider_id: number;
  provider_service_id?: number | null;
  shipping_order_id?: string | null;
  provider_name?: string;
  provider_service_name?: string;
  integrated_at?: string;
}

// ─── CRUD Payloads ───────────────────────────────────────────────

export type InvoiceItemPayload = Omit<InvoiceItem, 'invoice_item_id' | 'invoice_id' | 'created_at' | 'updated_at' | 'created_by' | 'last_updated_by'>;

/**
 * Dados de etiqueta de transporte (InvoiceEtiqueta gRPC)
 */
export interface InvoiceEtiquetaPayload {
  nome: string;
  endereco: string;
  numero: string;
  complemento?: string;
  municipio: string;
  uf: string;
  cep: string;
  bairro: string;
}

/**
 * Dados de volume para transporte (InvoiceVolumeDados gRPC)
 * especie: 1=Outro, 2=Volume, 3=Unidade, 4=Caixa, 5=Pacote, 6=Envelope, 7=Pallet, 8=Saco
 */
export interface InvoiceVolumePayload {
  quantidade: number;
  especie: number;
  numero?: string;
  peso_bruto: number;
  peso_liquido: number;
}

/**
 * Dados do veículo de transporte (InvoiceVeiculo gRPC)
 */
export interface InvoiceVeiculoPayload {
  placa: string;
  uf: string;
  marca?: string;
}

/**
 * Endereço do transportador (InvoiceTransportadorEndereco gRPC)
 */
export interface InvoiceTransportadorEnderecoPayload {
  endereco: string;
  municipio: string;
  uf: string;
}

/**
 * Dados do transportador (InvoiceTransportador gRPC)
 */
export interface InvoiceTransportadorPayload {
  nome: string;
  numero_documento: string;
  ie?: string;
  endereco?: InvoiceTransportadorEnderecoPayload;
}

/**
 * Dados de transporte completo (InvoiceTransporte gRPC)
 * frete_por_conta: 0=CIF, 1=FOB, 2=Terceiros, 3=Próprio Remetente, 4=Próprio Destinatário, 9=Sem transporte
 */
export interface InvoiceTransportePayload {
  frete_por_conta: number;
  frete?: number;
  veiculo?: InvoiceVeiculoPayload;
  transportador?: InvoiceTransportadorPayload;
  volume?: InvoiceVolumePayload;
  etiqueta?: InvoiceEtiquetaPayload;
}

export interface InvoiceCreatePayload {
  company_id: number;
  invoice_transaction_type_id: number;
  order_id?: number;
  /**
   * Vínculo com um Pedido de Entrega (convite de evento): NF de bonificação manual
   * (sem pedido de venda); após a emissão o backend grava DeliveryOrder.InvoiceId.
   */
  delivery_order_id?: number;
  /**
   * Modelo fiscal da NF (55 = NF-e, 65 = NFC-e). Default 55 quando omitido.
   */
  model?: number;
  /**
   * Quando `true`, indica que a NF emitida \u00e9 de **Remessa em bonifica\u00e7\u00e3o,
   * doa\u00e7\u00e3o ou brinde** referente aos itens com `is_gift = true` do pedido.
   * Backend deve gravar a refer\u00eancia em `Order.GiftInvoiceId` em vez de
   * `Order.InvoiceId`.
   */
  is_gift_invoice?: boolean;
  customer_id?: number;
  tipo: number; // 0=Entrada, 1=Saída
  finalidade?: number; // 1=Normal, 2=Complementar, 3=Ajuste, 4=Devolução
  observacoes?: string;
  customer_name: string;
  customer_document: string;
  customer_tipo_pessoa: string; // F=Física, J=Jurídica, E=Estrangeira
  customer_contribuinte: number; // 1=Contribuinte ICMS, 2=Isento, 9=Não contribuinte
  customer_ie?: string;
  customer_phone?: string;
  customer_email?: string;
  ship_addr_street?: string;
  ship_addr_num?: string;
  ship_addr_compl?: string;
  ship_addr_district?: string;
  ship_addr_city?: string;
  ship_addr_state?: string;
  ship_addr_zip?: string;
  shipping_service_name?: string;
  volume_quantity?: number;
  total_weight?: number;
  is_simple_tax?: boolean;
  provider_id?: number;
  provider_service_id?: number;
  frete_por_conta?: number;
  frete?: number;
  desconto?: number;
  /** Parcelas do pedido (incluindo parcela de crédito de troca) */
  installments?: { value: number; due_date: string; payment_method_id?: number; payment_method_name?: string; notes?: string }[];
  etiqueta?: InvoiceEtiquetaPayload;
  volume?: InvoiceVolumePayload;
  transporte?: InvoiceTransportePayload;
  items: InvoiceItemPayload[];
}

/**
 * Resposta de emissão de nota fiscal via integração Bling
 *
 * Cenários:
 *   HTTP 200 success=true  → data preenchido (NF criada com sucesso)
 *   HTTP 200 success=false → error é InvoiceEmitError (objeto com type/message/description/fields)
 *   HTTP 400              → error é string ("Bad Request"), details com mapa campo→mensagem
 *   HTTP 500              → error é string ("Internal Server Error"), message com descrição
 */
export interface InvoiceEmitResponse {
  success: boolean;
  data?: {
    id?: number;       // legado (Bling)
    invoice_id?: number;
    numero: string | number;
    serie: string | number;
    chave_acesso?: string;
    status_id?: number;
    protocolo?: string;
    contato?: { nome: string };
    // NF-e em processamento na SEFAZ (lote 103/105): número reservado, status 8, sem protocolo.
    // O backend conclui sozinho (processo nfe_pending_authorization) — RN-NFE-68.
    pending?: boolean;
    recibo?: string;
    message?: string;
  };
  // HTTP 200 com success=false — erro de negócio / Bling / SEFAZ (objeto)
  // HTTP 400/500 — erro genérico (string)
  error?: InvoiceEmitError | string;
  // HTTP 400 — mensagem geral de validação ("Validation failed")
  message?: string;
  // HTTP 400 — campos obrigatórios ausentes ou inválidos
  details?: Record<string, string>;
}

export interface InvoiceEmitError {
  type: string;
  message: string;
  description: string;
  fields?: InvoiceEmitErrorField[];
}

export interface InvoiceEmitErrorField {
  code: number;
  msg: string;
  element: string;
  namespace: string;
  collection?: InvoiceEmitErrorField[];
}

// ─── Sync Invoice (sincronização com Bling) ──────────────────────

export interface SyncInvoiceData {
  invoice_id: number;
  erp_invoice_id: number;
  numero: string;
  serie: number;
  chave_acesso: string;
  status: string;
  status_id: number;
  created: boolean;
}

export interface SyncInvoiceResponse {
  success: boolean;
  data?: SyncInvoiceData;
  error?: {
    type: string;
    message: string;
    description: string;
  };
}

export interface InvoiceUpdatePayload {
  customer_name?: string;
  customer_document?: string;
  customer_address?: string;
  customer_phone?: string;
  order_num_ecommerce?: number;
  shipping_service_name?: string;
  volume_quantity?: number;
  total_weight?: number;
  ship_addr_street?: string;
  ship_addr_num?: string;
  ship_addr_compl?: string;
  ship_addr_district?: string;
  ship_addr_city?: string;
  ship_addr_state?: string;
  ship_addr_zip?: string;
  ship_cust_email?: string;
  product_amount?: number;
  total_amount?: number;
  is_simple_tax?: boolean;
  provider_id?: number;
  provider_service_id?: number;
  frete_por_conta?: number;
  frete?: number;
  etiqueta?: InvoiceEtiquetaPayload;
  volume?: InvoiceVolumePayload;
}

